<template>
  <main class="gs-app gm-studio">
    <gs-card class="gm-header gm-page-header">
      <div>
        <p class="gm-eyebrow">GrooveMail</p>
        <h1>Email templates</h1>
        <p class="gm-muted">
          Customize a template for your next broadcast, sequence or signup email.
        </p>
      </div>
      <gs-button @click="choose(blank)"
        ><gs-icon name="plus" /> New template</gs-button
      >
    </gs-card>
    <email-gallery ref="gallery" :initial-collection="$route.query.collection || 'professional'" @choose="choose" />
    <gs-modal
      :value="open"
      @input="requestClose"
      title="Your email template"
      subtitle="Customize a copy. The original design stays available."
      size="xl"
      :close-on-backdrop="false"
      ><nav class="gm-tabs" aria-label="Template setup">
        <button
          v-for="(label, index) in steps"
          :key="label"
          :class="{ active: step === index }"
          @click="step = index"
        >
          {{ index + 1 }}. {{ label }}
        </button>
      </nav>
      <p v-if="error" class="gm-error" role="alert">{{ error }}</p>
      <div v-if="draft && step === 0" class="gm-stack">
        <label class="gm-field"
          >Template name<input v-model="draft.name" maxlength="160"/></label
        ><label class="gm-field"
          >Subject line<input v-model="draft.subject" maxlength="300"/></label
        ><label class="gm-field"
          >Preview text<input v-model="draft.previewText" maxlength="500"
        /></label>
        <p class="gm-muted">
          These stay with your template. When using a template in a mailing,
          check the mailing’s subject and preview text before sending.
        </p>
      </div>
      <email-document-editor
        v-if="draft"
        v-show="step === 1"
        :key="draft.id"
        :template="draft"
        @change="changed"
      />
      <div v-if="draft && step === 2" class="gm-stack">
        <h2>{{ draft.name }}</h2>
        <p><strong>Subject:</strong> {{ draft.subject || 'Not set' }}</p>
        <p class="gm-muted">{{ draft.previewText }}</p>
        <iframe
          class="gm-preview-frame"
          :srcdoc="preview(draft.bodyHtml)"
          sandbox=""
          title="Template final preview"
        />
        <p class="gm-muted">
          After saving, choose this design from My templates in any basic or
          HTML mailing editor. GrooveMail’s sending settings and unsubscribe
          footer are unchanged.
        </p>
      </div>
      <div class="gm-actions" style="margin-top:20px">
        <gs-button
          variant="outline"
          @click="step ? step-- : requestClose(false)"
          >{{ step ? 'Back' : 'Cancel' }}</gs-button
        ><gs-button v-if="step < 2" @click="step++">Continue</gs-button
        ><gs-button v-else :disabled="saving" @click="save">{{
          saving ? 'Saving…' : 'Save template'
        }}</gs-button
        ><gs-button
          v-if="draft && savedId"
          variant="outline"
          @click="
            savedId = '';
            draft.name += ' (copy)';
            step = 0;
          "
          >Save as a copy</gs-button
        ><gs-button
          v-if="draft && savedId"
          variant="danger"
          @click="archiveConfirm = true"
          >{{
            draft.status === 'archived'
              ? 'Restore template'
              : 'Archive template'
          }}</gs-button
        >
      </div></gs-modal
    >
    <gs-modal v-model="archiveConfirm" title="Change template availability?"
      ><p>
        {{
          draft && draft.status === 'archived'
            ? 'Restore this template to My templates.'
            : 'This hides the template from normal selection. Existing messages are not changed.'
        }}
      </p>
      <div class="gm-actions">
        <gs-button variant="outline" @click="archiveConfirm = false"
          >Cancel</gs-button
        ><gs-button
          variant="danger"
          @click="
            draft.status = draft.status === 'archived' ? 'active' : 'archived';
            archiveConfirm = false;
            save();
          "
          >Confirm</gs-button
        >
      </div></gs-modal
    >
    <gs-modal
      :value="discardConfirm"
      title="Discard template changes?"
      @input="keepEditing"
      ><p>
        Your saved template and the original professional design will stay
        unchanged.
      </p>
      <div class="gm-actions">
        <gs-button variant="outline" @click="keepEditing"
          >Keep editing</gs-button
        ><gs-button variant="danger" @click="discard"
          >Discard changes</gs-button
        >
      </div></gs-modal
    >
  </main>
</template>
<script>
import './index';
import axios from 'axios';
import EmailGallery from './EmailGallery.vue';
import EmailDocumentEditor from './EmailDocumentEditor.vue';
import templates from './data/email-templates.json';
import { editableTemplateId } from './template-library';
import {
  clone,
  errorMessage,
  validateEmailHtml,
  validateTemplateContent,
  previewDocument
} from './contracts';
export default {
  middleware: 'auth',
  layout: 'defaultWithoutVuePerfectScrollbar',
  components: { EmailGallery, EmailDocumentEditor },
  data: () => ({
    blank: templates[templates.length - 1],
    draft: null,
    savedId: '',
    open: false,
    step: 0,
    steps: ['Template info', 'Design email', 'Preview & save'],
    error: '',
    saving: false,
    archiveConfirm: false,
    discardConfirm: false,
    savedDraft: '',
    pendingRoute: ''
  }),
  computed: {
    dirty() {
      return this.draft && JSON.stringify(this.draft) !== this.savedDraft;
    }
  },
  mounted() {
    window.addEventListener('beforeunload', this.beforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    if (this.dirty) {
      this.pendingRoute = to.fullPath;
      this.requestClose(false);
      next(false);
    } else next();
  },
  methods: {
    beforeUnload(event) {
      if (this.dirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    },
    requestClose(value) {
      if (value) return;
      this.open = false;
      if (this.dirty) this.discardConfirm = true;
      else this.draft = null;
    },
    keepEditing() {
      this.discardConfirm = false;
      this.pendingRoute = '';
      this.open = true;
    },
    discard() {
      this.draft = null;
      this.open = false;
      this.discardConfirm = false;
      if (this.pendingRoute) this.$router.push(this.pendingRoute);
      this.pendingRoute = '';
    },
    preview: previewDocument,
    choose(template) {
      this.draft = clone(template);
      this.savedId = editableTemplateId(template);
      this.draft.status = template.status || 'active';
      this.savedDraft = JSON.stringify(this.draft);
      this.error = '';
      this.step = 0;
      this.open = true;
    },
    changed(result) {
      this.draft = Object.assign({}, this.draft, result);
    },
    async save() {
      this.error = '';
      if (!this.draft.name.trim()) {
        this.step = 0;
        this.error = 'Give your template a name.';
        return;
      }
      this.saving = true;
      try {
        validateTemplateContent(this.draft.bodyHtml);
        validateEmailHtml(this.draft.bodyHtml);
        if (this.savedId)
          await axios.put(
            '/studio/email-templates/' + this.savedId,
            this.draft
          );
        else await axios.post('/studio/email-templates', this.draft);
        this.savedDraft = JSON.stringify(this.draft);
        this.open = false;
        await this.$refs.gallery.load();
        this.$refs.gallery.collection = 'saved';
      } catch (error) {
        this.error = errorMessage(error);
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
